import React, { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { App, Modal } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'

import Api from '@vacationtracker/shared/services/api'
import { notificationStore } from '../../context/notificationsContext/store'
import { track } from '../../services/analytics/analytics'
import IntlMessages from '../../util/IntlMessages'

import { FrontendUrls } from '../../types/urls'
import { IAddon } from '@vacationtracker/shared/types/addons'

export interface UseAddonActionsParams {
  allSubscribedAddons: IAddon[]
  amIAdmin: boolean
  onActivated?: () => void
}

export interface AddonActions {
  activate: (type: string) => void
  modals: React.ReactNode
}

export const useAddonActions = ({ allSubscribedAddons, amIAdmin, onActivated }: UseAddonActionsParams): AddonActions => {
  const history = useHistory()
  const { notification } = App.useApp()
  const { formatMessage } = useIntl()

  const [visibleSubscribeFreeAddon, setVisibleSubscribeFreeAddon] = useState<boolean>(false)
  const [selectedAddonType, setSelectedAddonType] = useState<string | null>()
  const [isLoadingButtonAddon, setIsLoadingButtonAddon] = useState<boolean>(false)
  const [correlationId, setCorrelationId] = useState<string | null>()

  const { actionNotifications, setActionNotifications } = useContext(notificationStore)

  useEffect(() => {
    if (correlationId && !actionNotifications.includes(correlationId) && isLoadingButtonAddon) {
      setVisibleSubscribeFreeAddon(false)
      setIsLoadingButtonAddon(false)
      setCorrelationId(null)
      onActivated?.()
      // if selected addon is visibility, redirect to view page
      history.push(`${FrontendUrls.addons}/${selectedAddonType?.toLowerCase().replace(/_/g, '-')}/${selectedAddonType === 'VISIBILITY' ? 'view' : 'create'}`)
    }
  }, [actionNotifications])

  const showErrorNotification = (error) => {
    const errorDescription = error.response?.data?.message ? error.response?.data.message : error.message ? error.message : JSON.stringify(error)
    notification.error({
      message: formatMessage({ id: 'error.generic' }),
      description: errorDescription,
      duration: 0,
    })
  }

  const activate = (type: string) => {
    setSelectedAddonType(type)
    setVisibleSubscribeFreeAddon(true)
  }

  const automationAddonsUpdate = async () => {
    if (!amIAdmin) return
    try {
      setIsLoadingButtonAddon(true)
      const response = await Api.post('/core/event', {
        eventType: 'ADDONS_UPDATED',
        eventGroup: 'BILLING',
        type: selectedAddonType,
        isActive: true,
      })

      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'automations.activateInProgress' }),
        description: formatMessage({ id: 'automations.activateInProgressDescription' }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])

      setCorrelationId(response.correlationId as string)

      if (allSubscribedAddons.find((addon) => addon.type === selectedAddonType)?.status === 'CANCELED') {
        track('ADDON_RESUBSCRIBED', { addonName: selectedAddonType })
      } else {
        track('ADDON_SUBSCRIBED', { addonName: selectedAddonType })
      }
    } catch (error) {
      showErrorNotification(error)
      setIsLoadingButtonAddon(false)
    }
  }

  const modals = (
    <>
      <Modal
        title={amIAdmin ? <IntlMessages id="automations.subscripteToAddons" /> : <IntlMessages id="automations.onlyAdminCanEnableAddons" />}
        open={visibleSubscribeFreeAddon}
        onCancel={() => {
          setVisibleSubscribeFreeAddon(false)
          setSelectedAddonType(null)
        }}
        onOk={() => {
          if (amIAdmin) {
            automationAddonsUpdate()
            setVisibleSubscribeFreeAddon(false)
          } else {
            setVisibleSubscribeFreeAddon(false)
          }
        }}
        okText={amIAdmin ? <IntlMessages id="app.confirm" /> : <IntlMessages id="app.ok" />}
        cancelText={<IntlMessages id="app.close" />}
        confirmLoading={isLoadingButtonAddon}
      >
        <>
          {amIAdmin && <IntlMessages id="automations.addFreeAddonsDescirptionForAdmin" />}
          {!amIAdmin && <IntlMessages id="automations.addFreeAddonsDescirptionForApprover" />}
        </>
      </Modal>
    </>
  )

  return { activate, modals }
}
